import React, { useCallback, useContext, useEffect, useState } from "react";
import classnames from "classnames";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faNotEqual } from "@fortawesome/free-solid-svg-icons";
import * as notify from "../../../shared/notify";

import MissingMatch from "./missing-match";
import Modal from "../../modal";
import ProductMatch from "../../product-match";

import { findMatches } from "../lib/productData";
import { mapToReferenceProduct, sendMatchData, validateMatchV2 } from "../lib/finalData";
import { deleteAttribute } from "components/attributes/lib/attribute-data";
import { getImageUrl, getMatchOptions } from "components/final-qa/lib/productData";
import { lookupUUID } from "shared/attribute-uuid";
import { TaskContext } from "contexts/task";
import { AttributesContext } from "contexts/attributes";
import { CompareImages } from "../../product-match/components/compare-images";
import { ZoomCompareImages } from "../../product-match/components/zoom-compare-images";

const productMatchUUID = lookupUUID("productMatch");
const cvidUUID = lookupUUID("cvid");
let prevCvid = "";

const MatchDisplay = (props) => {
    const images = props.productMatches.map((m) => {
        const src = m.referenceImageId ? getImageUrl(m.referenceImageId, { size: 150 }) : "/images/noimage.jpg";

        return (
            <img
                key={`image_${m.referenceImageId}`}
                src={src}
                alt="CES Match"
                onClick={props.onClick}
            />
        );
    });

    return (
        <div className={classnames("match-image", { "-disabled": props.isComplete })}>
            <h4 className="-bar">{props.matchType.text} of:</h4>
            <div className={classnames("image-border", { "-double-columns": props.productMatches.length > 1 })}>{images}</div>
        </div>
    );
};

const ProductMatchModal = (props) => {
    const { task } = useContext(TaskContext);
    const [selectedProductCompare, setProductCompare] = useState();
    const [step, setStep] = useState("PRODUCT_MATCH");
    const attributes = useContext(AttributesContext);
    const codingWipKey = task.context.codingWipKey;
    const [showModal, setShowModal] = useState(false);
    const [productMatches, setProductMatches] = useState([]);
    const [matchType, setMatchType] = useState("");
    const [state, setState] = useState({
        products: [],
        productCount: 0,
        matchOptions: [],
        isLoading: true,
    });

    // don't show "Coding" shots im compares viewd
    const intakeProductImages = props.shots.filter(({ imageView, skipEditing }) => imageView !== "Coding" && !skipEditing);

    useEffect(() => {
        prevCvid = attributes.data[cvidUUID];
        fetchMatches();
    }, []);

    useEffect(() => {
        if (attributes.data[cvidUUID] !== prevCvid) {
            prevCvid = attributes.data[cvidUUID];
            fetchMatches();
        } else if (matchType && !attributes.data[productMatchUUID]) {
            fetchMatches();
        }
    }, [attributes]);

    useEffect(() => {
        if (!state.isLoading && productMatches.length > 0 && state.productCount === 0) {
            setShowModal(true);
        }
    }, [state.isLoading, productMatches]);

    const fetchMatches = async () => {
        const products = await findMatches(task.upc, codingWipKey, attributes.data[cvidUUID]);
        const options = await getMatchOptions();
        prevCvid = attributes.data[cvidUUID];

        setState({
            products: products,
            productCount: products.length,
            matchOptions: options,
            isLoading: false,
        });

        if (products.length === 0) {
            props.setIsValid(true);
        }

        setFromAttributes(options);
    };

    const setFromAttributes = (options) => {
        if (matchType && !attributes.data[productMatchUUID]) {
            setMatchType("");
            setProductMatches([]);

            return;
        }

        const match = attributes.data[productMatchUUID];
        const matchingProducts = match && match.matchingProducts ? match.matchingProducts : [];
        const type = match && match.type ? options.find((o) => o.value.toString() === match.type.toString()) : "";

        setMatchType(type);
        setProductMatches(matchingProducts);

        if (match) {
            props.setIsValid(true);
        }
    };

    const openModal = (e) => {
        if (props.isComplete) {
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        setShowModal(true);
    };
    const closeModal = () => {
        setShowModal(false);
        setStep("PRODUCT_MATCH");
    };

    const saveMatchV2 = async (type, matchProducts, hasUserConfirmedOverridableErrors = false) => {
        const mappedMatchProducts = matchProducts.map(mapToReferenceProduct);
        const validateResponse = await validateMatchV2(codingWipKey, type, mappedMatchProducts, hasUserConfirmedOverridableErrors);

        if (!validateResponse) {
            return false;
        }

        if (validateResponse && !validateResponse.isValid) {
            if (validateResponse.overridableErrors && validateResponse.overridableErrors.length > 0) {
                const displayErrors = validateResponse.overridableErrors.join(", ");
                const confirmOverride = await notify.confirm(`${displayErrors}`);

                if (confirmOverride) {
                    await saveMatchV2(type, matchProducts, confirmOverride);

                    return true;
                } else {
                    return false;
                }
            }

            return false;
        }

        const response = await sendMatchData(codingWipKey, type, mappedMatchProducts, hasUserConfirmedOverridableErrors);

        if (!response) {
            return false;
        }

        const newType = type ? state.matchOptions.find((o) => o.value.toString() === type.toString()) : "";
        setMatchType(newType);
        setProductMatches(mappedMatchProducts);
        notify.success("Saved!");
        props.setIsValid(true);
        closeModal();

        return true;
    };

    const handleStep = useCallback((name) => setStep(name), []);

    const handleZoomCompare = useCallback(
        (selectedProductId) => {
            setStep("ZOOM_COMPARE");
            setProductCompare(state.products.find(({ productId }) => productId === selectedProductId));
        },
        [state.products]
    );

    const deleteMatch = async () => {
        const response = await deleteAttribute(codingWipKey, props.taskKey, productMatchUUID);

        if (!response) {
            return;
        }

        setMatchType("");
        setProductMatches([]);
        notify.success("Saved!");
        const isValid = state.productCount === 0;
        props.setIsValid(isValid);
        closeModal();
    };

    if (state.isLoading) {
        return null;
    }

    const matchIds = productMatches.map((m) => m.referenceProductId);
    const matchCount = <div className="match-count">{state.products.length}</div>;
    const matchTypeValue = matchType ? matchType.value : "";
    const modalButton = matchType ? (
        productMatches.length > 0 ? (
            <MatchDisplay
                productMatches={productMatches}
                matchType={matchType}
            />
        ) : (
            <div className="no-matches">
                <h4>No Match</h4>
                <FontAwesomeIcon icon={faNotEqual} />
            </div>
        )
    ) : (
        <div className="potential-matches">
            <h4>Product Matches</h4>
            {matchCount}
        </div>
    );

    return (
        <>
            <Modal
                show={showModal}
                onClose={closeModal}
            >
                {step === "IMAGE_COMPARE" && (
                    <CompareImages
                        upc={task.upc}
                        onStepChange={handleStep}
                        matchedProducts={state.products}
                        productShots={intakeProductImages}
                        onZoomCompare={handleZoomCompare}
                    />
                )}
                {step === "ZOOM_COMPARE" && (
                    <ZoomCompareImages
                        intakeProductId={task.context.correlationId}
                        productShots={intakeProductImages}
                        selectedProductCompare={selectedProductCompare}
                        onStepChange={handleStep}
                        reference={{
                            shipmentKey: task.context.shipmentKey,
                            shipmentProductKey: task.context.shipmentProductKey,
                            photographyWipKey: task.context.photographyWipKey,
                        }}
                    />
                )}
                {step === "PRODUCT_MATCH" && (
                    <>
                        {state.products.length <= 0 && !props.isComplete && (
                            <MissingMatch
                                deleteMatch={deleteMatch}
                                closeModal={closeModal}
                            />
                        )}
                        {state.products.length > 0 && attributes && (
                            <ProductMatch
                                photoKey={task.context.photographyWipKey}
                                codingWipKey={codingWipKey}
                                matchIds={matchIds}
                                matchTypeValue={matchTypeValue}
                                matchOptions={state.matchOptions}
                                products={state.products}
                                upc={task.upc}
                                attributes={attributes.data}
                                shipmentKey={task.shipmentKey}
                                shipmentProductKey={task.shipmentProductKey}
                                saveMatch={saveMatchV2}
                                deleteMatch={deleteMatch}
                                onStepChange={handleStep}
                            />
                        )}
                    </>
                )}
            </Modal>
            <div
                className={classnames("duplicate-product", {
                    "-disabled": props.isComplete || (!productMatches.length > 0 && state.productCount === 0),
                })}
                onClick={(productMatches.length > 0 && state.productCount === 0) || state.productCount > 0 ? openModal : null}
            >
                {modalButton}
            </div>
        </>
    );
};

export default ProductMatchModal;
